import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import {
  VENUE_EVENTS,
  type ResourceSessionEventPayload,
  type TableEventPayload,
} from '@/lib/shared-types';
import { useRealtime } from '@/Hooks/useRealtime';
import { getSocket, onSocketChange, socketGeneration } from '@/lib/realtime';
import { useAppDispatch } from '@/Redux/store';
import type { AppDispatch } from '@/Redux/store';
import {
  applyTableStateEvent,
  markLiveStateUnverified,
  resyncLiveState,
  spacesApi,
} from './spacesApi';

/**
 * §27 event stream → the spaces cache.
 *
 * One subscription, in one place, writing to the shared cache — instead of each
 * screen listening and patching its own local `useState`. That was the old
 * shape and it had a specific failure: the floor map repainted table 12 and the
 * table list two clicks away still showed it free, because only the screen
 * holding the listener heard anything. Here the event lands in the cache, and
 * every subscriber of every affected query rerenders from it.
 *
 * Mount this once per screen that shows live spaces data. It is ref-counted, so
 * several screens may hold it at once and only the last one to leave marks the
 * cache unverified.
 */

/** §12 — a resource's map colour follows its session, not the session's own name. */
function resourceStatusFor(sessionStatus: string): string {
  if (sessionStatus === 'ACTIVE') return 'IN_USE';
  if (sessionStatus === 'PAUSED') return 'PAUSED';
  return 'AVAILABLE';
}

// ---------------------------------------------------------------------------
// Connection watch — shared, ref-counted
//
// Two different silences look identical on screen and both mean the same thing:
// the socket dropped, or no component is listening any more. In either case
// events are landing nowhere and the states in cache start drifting from the
// venue. Both mark the cache unverified; only a fetch clears it.
// ---------------------------------------------------------------------------

let watchers = 0;
let detachSocket: (() => void) | null = null;
let sawDisconnect = false;
/** Kept so the socket-replacement handler can rebind without a component. */
let watchDispatch: AppDispatch | null = null;

/*
 * Re-attach when the socket is replaced.
 *
 * This watch is what turns a reconnect into a refetch, and it held the same
 * stale reference the hook did: after a replacement it listened to an object
 * that would never emit again, so the gap it exists to close silently stopped
 * being closed.
 *
 * Subscribed on FIRST ATTACH rather than at module scope. A module-level
 * `onSocketChange` would run on import, so merely importing this slice — which
 * several screens do transitively — would build a socket and register a
 * listener, and every test that mocks `@/lib/realtime` would have to know about
 * it. A shared, ref-counted watch has no component to hang off, so it hangs off
 * its own first use instead.
 */
let releaseGenerationWatch: (() => void) | null = null;
let watchedGeneration = 0;

function onSocketReplaced(): void {
  if (socketGeneration() === watchedGeneration) return;
  watchedGeneration = socketGeneration();
  if (!detachSocket || !watchDispatch) return;
  detachSocket();
  detachSocket = null;
  // A replacement is a gap by definition: anything that arrived while the old
  // socket was dying reached nobody, so the next connect has a hole behind it.
  sawDisconnect = true;
  bindConnectionWatch(watchDispatch);
}

function attachConnectionWatch(dispatch: AppDispatch) {
  watchers += 1;
  if (detachSocket) return;
  if (!releaseGenerationWatch) {
    watchedGeneration = socketGeneration();
    releaseGenerationWatch = onSocketChange(onSocketReplaced);
  }
  bindConnectionWatch(dispatch);
}

function bindConnectionWatch(dispatch: AppDispatch) {
  watchDispatch = dispatch;
  const socket = getSocket();

  const onDisconnect = () => {
    sawDisconnect = true;
    dispatch(markLiveStateUnverified());
  };

  const onConnect = () => {
    // The first connect is not a gap — the queries fetched alongside it. Only a
    // *re*connect has a hole behind it, and `connectRealtime` replays rooms but
    // never the events missed while away, so the hole can only be refilled by
    // asking the server again.
    if (!sawDisconnect) return;
    sawDisconnect = false;
    dispatch(resyncLiveState());
  };

  socket.on('connect', onConnect);
  socket.on('disconnect', onDisconnect);
  detachSocket = () => {
    socket.off('connect', onConnect);
    socket.off('disconnect', onDisconnect);
  };
}

function releaseConnectionWatch(dispatch: AppDispatch) {
  watchers = Math.max(0, watchers - 1);
  if (watchers > 0) return;
  detachSocket?.();
  detachSocket = null;
  // Released with the last watcher, so an idle tab holds nothing at all.
  releaseGenerationWatch?.();
  releaseGenerationWatch = null;
  watchDispatch = null;
  // Nothing is listening from this instant on. Whatever is in `states` is a
  // photograph, and the next screen to mount must refetch rather than trust it.
  dispatch(markLiveStateUnverified());
}

export interface SpacesRealtimeOptions {
  /**
   * Resource colours, for a screen that still holds its own resource list.
   * The cache keeps table state; resources are invalidated by tag here, and
   * this callback lets a screen repaint in the same tick rather than waiting
   * for its own refetch.
   */
  onResourceStatus?: (resourceId: string, status: string) => void;
  /** Rooms to join. Defaults, via `useRealtime`, to the active branch. */
  rooms?: string[];
}

export interface SpacesRealtime {
  /** Whether the socket is up. False means everything on screen is unverified. */
  connected: boolean;
}

export function useSpacesRealtime(options?: SpacesRealtimeOptions): SpacesRealtime {
  const dispatch = useAppDispatch();
  const [connected, setConnected] = useState(() => getSocket().connected);

  const resourceCallback = useRef(options?.onResourceStatus);
  resourceCallback.current = options?.onResourceStatus;

  /**
   * A table changed state. Patched into the cache from the payload rather than
   * refetched — the event already says which table and which state, and a round
   * trip would leave the map wrong for its duration (§5.2 wants the colour
   * inside a second). Revalidation on mount or reconnect confirms it.
   */
  const onTable = useCallback(
    (payload: TableEventPayload) => {
      dispatch(applyTableStateEvent(payload.tableId, payload.state));
    },
    [dispatch],
  );

  /**
   * A resource session moved.
   *
   * The callback repaints the resource board, which reads `/resources` through
   * `useApi` and is out of RTK Query's reach. The dispatch beside it is no
   * longer the speculative half it was written as: the kids desk provides both
   * `Resource` and `ResourceSession` from `Pages/Spaces/kidsStationApi.ts`, so
   * a session starting anywhere in the venue now refetches the desk's board.
   */
  const onResourceSession = useCallback(
    (payload: ResourceSessionEventPayload) => {
      resourceCallback.current?.(payload.resourceId, resourceStatusFor(payload.status));
      dispatch(
        spacesApi.util.invalidateTags([
          { type: 'Resource', id: payload.resourceId },
          { type: 'Resource', id: 'LIST' },
          'ResourceSession',
        ]),
      );
    },
    [dispatch],
  );

  useRealtime<TableEventPayload>(VENUE_EVENTS.TABLE_STATUS_CHANGED, onTable, options?.rooms);
  useRealtime<ResourceSessionEventPayload>(
    VENUE_EVENTS.RESOURCE_SESSION_STARTED,
    onResourceSession,
    options?.rooms,
  );
  useRealtime<ResourceSessionEventPayload>(
    VENUE_EVENTS.RESOURCE_SESSION_PAUSED,
    onResourceSession,
    options?.rooms,
  );
  useRealtime<ResourceSessionEventPayload>(
    VENUE_EVENTS.RESOURCE_SESSION_ENDED,
    onResourceSession,
    options?.rooms,
  );

  /*
   * The socket's identity, not only its state. `getSocket()` returns a DIFFERENT
   * object after `onAuthFailure` drops a dead socket or a human presses
   * Reconnect, and listeners bound at mount stay on the one that was replaced —
   * so `connected` froze at false while the live socket delivered events
   * normally, and every board using this hook flew a permanent RECONNECTING
   * banner over data that was in fact current.
   */
  const generation = useSyncExternalStore(onSocketChange, socketGeneration, socketGeneration);

  useEffect(() => {
    const socket = getSocket();
    const up = () => setConnected(true);
    const down = () => setConnected(false);
    socket.on('connect', up);
    socket.on('disconnect', down);
    setConnected(socket.connected);

    attachConnectionWatch(dispatch);
    return () => {
      socket.off('connect', up);
      socket.off('disconnect', down);
      releaseConnectionWatch(dispatch);
    };
  }, [dispatch, generation]);

  return { connected };
}
